import {
  FormControl,
  Grid,
  MenuItem,
  Box,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { Country, State } from "country-state-city";
import {
  postcodeValidator,
  postcodeValidatorExistsForCountry,
} from "postcode-validator";
import { ProjectLineItem } from "@interfaces/project";
import { ChangeEvent, useState } from "react";
import { StyledTextField } from "@stories/atoms/StyledTextField/StyledTextField";
import { StyledButton } from "@stories/atoms/StyledButton/StyledButton";
import { StyledCard } from "@stories/atoms/StyledCard/StyledCard";
import { SubTitle } from "@stories/atoms/SubTitle/SubTitle";

interface SiteOfficeFormProps {
  project: ProjectLineItem;
  handleBack: () => void;
  handleNext: () => void;
  setProject: (project: ProjectLineItem) => void;
}

export const SiteOfficeForm = ({
  project,
  setProject,
  handleBack,
  handleNext,
}: SiteOfficeFormProps) => {
  const [sameAsProjectAddress, setSameAsProjectAddress] = useState(
    project.siteAddressSameAsProject
  );

  const validationSchema = yup.object({
    siteAddressLine1: yup.string().required("Address Line 1 is required"),
    siteAddressLine2: yup.string(),
    siteCountry: yup.string().required("Country is required"),
    siteState: yup.string().required("State is required"),
    siteCity: yup.string().required("City is required"),
    sitePostCode: yup
      .string()
      .required("Post code is required")
      .test(
        "Valid for country",
        "Not a valid post code for country",
        function (item) {
          if (postcodeValidatorExistsForCountry(this.parent.siteCountry)) {
            const valid = postcodeValidator(item, this.parent.siteCountry);
            return valid;
          }
          return true;
        }
      ),
  });
  const formik = useFormik({
    initialValues: {
      siteAddressLine1: sameAsProjectAddress
        ? project.projectAddressLine1
        : project.siteAddressLine1,
      siteAddressLine2: sameAsProjectAddress
        ? project.projectAddressLine2
        : project.siteAddressLine2,
      siteCountry: sameAsProjectAddress
        ? project.projectCountry
        : project.siteCountry,
      siteState: sameAsProjectAddress
        ? project.projectState
        : project.siteState,
      siteCity: sameAsProjectAddress ? project.projectCity : project.siteCity,
      sitePostCode: sameAsProjectAddress
        ? project.projectPostCode
        : project.siteCity,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      setProject({
        ...project,
        siteAddressSameAsProject: sameAsProjectAddress,
        siteAddressLine1: values.siteAddressLine1,
        siteAddressLine2: values.siteAddressLine2,
        siteCountry: values.siteCountry,
        siteState: values.siteState,
        siteCity: values.siteCity,
        sitePostCode: values.sitePostCode,
      });
      handleNext();
    },
  });

  const countries = Country.getAllCountries();
  const states = State.getStatesOfCountry(formik.values.siteCountry);
  return (
    <StyledCard>
      <Grid container spacing={2} sx={{ padding: "5%" }}>
        <Grid item xs={12} md={12}>
          <SubTitle title={"Site Office Details"} />
        </Grid>
        <Grid item xs={12} md={12}>
          <Box>
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={12}>
                  <FormControlLabel
                    id={"sameAsProjectAddress"}
                    name={"sameAsProjectAddress"}
                    defaultChecked={sameAsProjectAddress}
                    control={
                      <Checkbox
                        checked={sameAsProjectAddress}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          if (e.target.checked) {
                            formik.setFieldValue(
                              "siteAddressLine1",
                              project.projectAddressLine1
                            );
                            formik.setFieldValue(
                              "siteAddressLine2",
                              project.projectAddressLine2
                            );
                            formik.setFieldValue(
                              "siteCountry",
                              project.projectCountry
                            );
                            formik.setFieldValue(
                              "siteState",
                              project.projectState
                            );
                            formik.setFieldValue(
                              "siteCity",
                              project.projectCity
                            );
                            formik.setFieldValue(
                              "sitePostCode",
                              project.projectPostCode
                            );
                          } else {
                            formik.setFieldValue(
                              "siteAddressLine1",
                              project.siteAddressLine1
                            );
                            formik.setFieldValue(
                              "siteAddressLine2",
                              project.siteAddressLine2
                            );
                            formik.setFieldValue(
                              "siteCountry",
                              project.siteCountry
                            );
                            formik.setFieldValue(
                              "siteState",
                              project.siteState
                            );
                            formik.setFieldValue("siteCity", project.siteCity);
                            formik.setFieldValue(
                              "sitePostCode",
                              project.sitePostCode
                            );
                          }
                          setSameAsProjectAddress(e.target.checked);
                        }}
                      />
                    }
                    label="Site office same as project?"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    id="siteAddressLine1"
                    name="siteAddressLine1"
                    label="Address Line 1"
                    fullWidth
                    margin="dense"
                    disabled={sameAsProjectAddress || formik.isSubmitting}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.siteAddressLine1}
                    error={
                      formik.touched.siteAddressLine1 &&
                      Boolean(formik.errors.siteAddressLine1)
                    }
                    helperText={
                      formik.touched.siteAddressLine1
                        ? formik.errors.siteAddressLine1
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    id="siteAddressLine2"
                    name="siteAddressLine2"
                    label="Address Line 2"
                    fullWidth
                    margin="dense"
                    disabled={sameAsProjectAddress || formik.isSubmitting}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.siteAddressLine2}
                    error={
                      formik.touched.siteAddressLine2 &&
                      Boolean(formik.errors.siteAddressLine2)
                    }
                    helperText={
                      formik.touched.siteAddressLine2
                        ? formik.errors.siteAddressLine2
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <StyledTextField
                      select
                      id="siteCountry"
                      name="siteCountry"
                      fullWidth
                      margin="dense"
                      disabled={sameAsProjectAddress || formik.isSubmitting}
                      value={formik.values.siteCountry}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.siteCountry &&
                        Boolean(formik.errors.siteCountry)
                      }
                      helperText={
                        formik.touched.siteCountry
                          ? formik.errors.siteCountry
                          : ""
                      }
                      label="Country"
                    >
                      {countries.map((c) => (
                        <MenuItem key={c.isoCode} value={c.isoCode}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </StyledTextField>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <StyledTextField
                      select
                      id="siteState"
                      name="siteState"
                      fullWidth
                      margin="dense"
                      disabled={sameAsProjectAddress || formik.isSubmitting}
                      value={formik.values.siteState}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.siteState &&
                        Boolean(formik.errors.siteState)
                      }
                      helperText={
                        formik.touched.siteState ? formik.errors.siteState : ""
                      }
                      label="State"
                    >
                      {states.map((s, index) => (
                        <MenuItem
                          key={`${s.isoCode}-${index}`}
                          value={s.isoCode}
                        >
                          {s.name}
                        </MenuItem>
                      ))}
                    </StyledTextField>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <StyledTextField
                      id="siteCity"
                      name="siteCity"
                      fullWidth
                      margin="dense"
                      disabled={sameAsProjectAddress || formik.isSubmitting}
                      value={formik.values.siteCity}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.siteCity &&
                        Boolean(formik.errors.siteCity)
                      }
                      helperText={
                        formik.touched.siteCity ? formik.errors.siteCity : ""
                      }
                      label="City"
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    id="sitePostCode"
                    name="sitePostCode"
                    label="Postcode"
                    fullWidth
                    margin="dense"
                    disabled={sameAsProjectAddress || formik.isSubmitting}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.sitePostCode}
                    error={
                      formik.touched.sitePostCode &&
                      Boolean(formik.errors.sitePostCode)
                    }
                    helperText={
                      formik.touched.sitePostCode
                        ? formik.errors.sitePostCode
                        : ""
                    }
                  />
                </Grid>
                <Grid
                  item
                  xs={6}
                  sm={6}
                  sx={{
                    display: "flex !important",
                    justifyContent: "left !important;",
                  }}
                >
                  <StyledButton variant="contained" onClick={handleBack}>
                    Back
                  </StyledButton>
                </Grid>
                <Grid
                  item
                  xs={6}
                  sm={6}
                  sx={{
                    display: "flex !important",
                    justifyContent: "right !important;",
                  }}
                >
                  <StyledButton variant="contained" type="submit">
                    Next
                  </StyledButton>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Grid>
      </Grid>
    </StyledCard>
  );
};
